import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatPence, SERVICES, useCheckBalance } from '@/features/payments';
import {
  BalanceDisplay,
  InsufficientBalance,
} from '@/features/payments/components/insufficient-balance';

interface EddModalProps {
  open: boolean;
  onPurchase: () => void;
  onSkip: () => void;
  onAddFunds: () => void;
  isPurchasing: boolean;
}

const PRICE_PENCE = 1500; // £15.00

/**
 * Enhanced Due Diligence Modal - shown when user wants to purchase EDD.
 * Charges £15.00 for full article scraping and in-depth analysis.
 */
export function EddModal({ open, onPurchase, onSkip, onAddFunds, isPurchasing }: EddModalProps) {
  const { data: balanceCheck, isLoading: isCheckingBalance } = useCheckBalance(
    SERVICES.AI_CONFIDENCE_REPORT,
    open,
  );

  const hasSufficientBalance = balanceCheck?.sufficient ?? false;
  const currentBalance = balanceCheck?.currentBalancePence ?? 0;

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[600px]" hideCloseButton>
        <DialogHeader className="pt-4 text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Enhanced Due Diligence
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Descriptive text */}
          <div className="space-y-3 text-[14px] leading-relaxed text-gray-600">
            <p>
              For a more thorough assessment of this client, we recommend performing an enhanced due
              diligence check. This will generate an in-depth AML Report with full article content
              and comprehensive analysis.
            </p>
            <p>
              The enhanced check includes full article content extraction, AI-powered relevance
              analysis, and comprehensive risk scoring.
            </p>
          </div>

          {/* Report offer */}
          <div className="rounded-lg border border-[#E5E7EB] p-4">
            <div className="flex items-start gap-3">
              <Search className="h-6 w-6 flex-shrink-0 text-[#131313]" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-medium text-[#051923]">
                    AI Agent Confidence Report
                  </span>
                  <span className="text-[18px] font-semibold text-[#131313]">
                    {formatPence(PRICE_PENCE)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance check */}
          {!isCheckingBalance &&
            (hasSufficientBalance ? (
              <BalanceDisplay currentBalancePence={currentBalance} />
            ) : (
              <InsufficientBalance
                currentBalancePence={currentBalance}
                requiredAmountPence={PRICE_PENCE}
                shortfall={balanceCheck?.shortfall ?? PRICE_PENCE}
                onAddFunds={onAddFunds}
              />
            ))}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={onPurchase}
            disabled={!hasSufficientBalance || isPurchasing || isCheckingBalance}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isPurchasing ? 'Processing...' : 'Add Enhanced Analysis (£15.00)'}
          </Button>
          <button
            type="button"
            onClick={onSkip}
            disabled={isPurchasing}
            className="text-[14px] font-medium text-gray-500 underline hover:text-gray-700"
          >
            Continue without enhanced check
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
